// An entity's audit trail as the last section of a section page, ported from
// ewt's entity-history.tsx (itself from the vouchers admin's
// entity-history-rail.tsx). It fetches once revealed: scrolled into view, or
// its nav entry clicked (the page forwards the shell's onNavClick through
// `useHistoryReveal`). The sheet is local state; the audit page owns
// `?eventId=`, reached through "Open in audit log", and shows the trail past
// the first page through "View all in audit log".

import { AuditTimeline } from "@pej/kore/components";
import type { AuditEvent } from "@pej/kore/types";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuditSearch } from "@/api/audit";
import { PageError, useRetry } from "@/components/page-states";
import { SectionCard } from "@/components/section-page/section-card";
import { AuditEventSheet } from "./audit-event-sheet";
import { type HistoryScope, historyRq, historySearch } from "./audit-filters";
import { summarizeKoreEvent, toKoreAuditEvent } from "./kore-audit-adapter";

const LINK_CLASS = "mt-3 inline-block text-sm font-medium text-kore-accent hover:underline";

/** Whether a page's History has been revealed, and the shell `onNavClick` that reveals it. */
export function useHistoryReveal(sectionId: string) {
    const [revealed, setRevealed] = useState(false);
    const reveal = useCallback(() => setRevealed(true), []);
    const onNavClick = useCallback(
        (id: string) => {
            if (id === sectionId) setRevealed(true);
        },
        [sectionId],
    );
    return { revealed, reveal, onNavClick };
}

export interface HistorySectionProps {
    workspaceId: string;
    sectionId: string;
    /** `null` while what the trail covers is still loading (a survey's placements). */
    scope: HistoryScope | null;
    revealed: boolean;
    onReveal: () => void;
}

export function HistorySection({ workspaceId, sectionId, scope, revealed, onReveal }: HistorySectionProps) {
    const sectionRef = useRef<HTMLElement>(null);
    const [eventId, setEventId] = useState<string | null>(null);

    useEffect(() => {
        const section = sectionRef.current;
        if (revealed || !section || typeof IntersectionObserver === "undefined") return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) onReveal();
        });
        observer.observe(section);
        return () => observer.disconnect();
    }, [revealed, onReveal]);

    const historyQuery = useAuditSearch(workspaceId, historyRq(scope ?? { groups: [] }), {
        enabled: revealed && scope !== null,
        keepPrevious: false,
    });
    const retry = useRetry(historyQuery);
    const events = useMemo(() => (historyQuery.data?.items ?? []).map(toKoreAuditEvent), [historyQuery.data]);

    const closeSheet = useCallback(() => setEventId(null), []);
    const renderMetadata = useCallback(
        (event: AuditEvent) => (
            <Link
                to="/w/$workspaceId/audit"
                params={{ workspaceId }}
                search={{ eventId: event.eventId }}
                className={LINK_CLASS}
            >
                Open in audit log
            </Link>
        ),
        [workspaceId],
    );

    return (
        <SectionCard ref={sectionRef} id={sectionId} title="History">
            {retry.failed ? (
                <PageError compact level={3} {...retry.props} />
            ) : (
                <div className="max-w-2xl">
                    <AuditTimeline
                        events={events}
                        currentEventId={eventId ?? undefined}
                        // Unrevealed reads as loading, never as an empty trail.
                        loading={historyQuery.data === undefined}
                        // Fast fetches never flash a skeleton.
                        loadingDelayMs={250}
                        onSelect={setEventId}
                        summarize={summarizeKoreEvent}
                    />
                    {scope && historyQuery.data?.nextPageToken ? (
                        <Link
                            to="/w/$workspaceId/audit"
                            params={{ workspaceId }}
                            search={historySearch(scope)}
                            className={LINK_CLASS}
                        >
                            View all in audit log
                        </Link>
                    ) : null}
                </div>
            )}
            <AuditEventSheet
                workspaceId={workspaceId}
                eventId={eventId}
                onNavigate={setEventId}
                onClose={closeSheet}
                onNotFound={closeSheet}
                renderMetadata={renderMetadata}
            />
        </SectionCard>
    );
}
